<template lang="pug">
ErrorAlert.ma-4(
  v-if="verseGridItems.error"
  :reporting="{ message: verseGridItems.error.message, extra: verseGridItems.error }"
  :stack="verseGridItems.error.stack"
  :message="verseGridItems.error.message || String(verseGridItems.error)"
)
HSection(v-else)
  VvGrid.fill-height(
    v-model:current-column="currentColumn"
    :num-columns="tids.length"
  )
    div.pa-2.bible-grid__translation-header(
      v-for="(tid, columnIndex) in tids"
      :key="`translation-${tid}-header`"
      :style="{ gridRowStart: 1, gridRowEnd: 2, gridColumnStart: columnIndex + 1, gridColumnEnd: columnIndex + 2, textAlign: 'center', backgroundColor: 'rgb(var(--v-theme-surface))' }"
    )
      div {{ allTranslations[tid]?.name ?? "?" }}

    div.pa-2(
      v-for="(tid, columnIndex) in tids"
      :key="`translation-${tid}-read-before`"
      :style="{ gridRowStart: 2, gridRowEnd: 3, gridColumnStart: columnIndex + 1, gridColumnEnd: columnIndex + 2, textAlign: 'center' }"
    )
      VBtn(
        v-if="versesAvailableBefore || displayedRangeExtendedBefore.isPending || displayedRangeExtendedBefore.error"
        variant="text"
        :icon="true"
        title="Read preceding verses"
        aria-label="Read preceding verses"
        :disabled="displayedRangeExtendedBefore.isPending"
        @click="addVersesAbove"
      )
        VIcon.mdi-spin(v-if="displayedRangeExtendedBefore.isPending") {{ mdiLoading }}
        VIcon(
          v-else-if="displayedRangeExtendedBefore.error"
          :title="displayedRangeExtendedBefore.error.message || displayedRangeExtendedBefore.error"
        ) {{ mdiAlert }}
        VIcon(v-else) {{ mdiChevronDoubleUp }}

    div(
      v-for="item in verseGridItems.value || []"
      :key="item.key"
      :class="item.classes"
      :style="{ ...item.styles, gridRowStart: item.grid.row.start + 2, gridRowEnd: item.grid.row.end + 2, gridColumnStart: item.grid.column.start, gridColumnEnd: item.grid.column.end }"
    )
      BibleVerse(
        v-if="item.verse"
        :verse="item.verse"
        :first="item.firstVerseInColumn"
        :show-actions="true"
        :column-style="true"
        :show-heading="true"
        :ref-number-outer="true"
        :show-study-button="true"
      )
      div.pa-2(
        v-else-if="item.markup"
        v-html="item.markup"
      )
      div.pa-2(v-else)

    div.pa-2(
      v-for="(tid, columnIndex) in tids"
      :key="`translation-${tid}-read-after`"
      :style="{ gridRowStart: 10000, gridRowEnd: 10001, gridColumnStart: columnIndex + 1, gridColumnEnd: columnIndex + 2, textAlign: 'center' }"
    )
      VBtn(
        v-if="versesAvailableAfter || displayedRangeExtendedAfter.isPending || displayedRangeExtendedAfter.error"
        variant="text"
        :icon="true"
        title="Read following verses"
        aria-label="Read following verses"
        :disabled="displayedRangeExtendedAfter.isPending"
        @click="addVersesBelow"
      )
        VIcon.mdi-spin(v-if="displayedRangeExtendedAfter.isPending") {{ mdiLoading }}
        VIcon(
          v-else-if="displayedRangeExtendedAfter.error"
          :title="displayedRangeExtendedAfter.error.message || displayedRangeExtendedAfter.error"
        ) {{ mdiAlert }}
        VIcon(v-else) {{ mdiChevronDoubleDown }}
      FleuronBudNorthWest.fleuron.my-8(v-else)
</template>

<script setup lang="ts">
/**
 * The bible component contains a grid displaying all verses.
 * It allows users to easily add and remove verses.
 */

import { computed } from "vue";
import type { VidRange } from "@rsc/scripture-util";
import {
  mdiAlert,
  mdiChevronDoubleDown,
  mdiChevronDoubleUp,
  mdiLoading,
} from "@mdi/js";
import { getVerseGridItems } from "./BibleGrid.util";
import { allTranslations } from "~/static/translations";
import { injectRequired } from "~/injectRequired";
import { useComputedResultAsync } from "~/composables/useComputedResultAsync";
import HSection from "~/headings/components/HSection.vue";
import { ScriptureClientKey } from "~/injectionKeys";

const emit = defineEmits<{
  "update:displayedRange": [value: VidRange];
}>();

const scriptureClient = injectRequired(ScriptureClientKey);

const props = defineProps<{
  /**
   * The range of verses to display.
   * The translation is ignored.
   */
  displayedRange: VidRange;

  /**
   * The IDs of the open translations.
   */
  tids: string[];

  /**
   * The ID of the default translation of the current locale.
   */
  localTid: string;
}>();

/**
 * The number of verses to add or remove at time.
 */
const verseIncrement = 10;

const runtimeConfig = useRuntimeConfig();

/**
 * The translation to use when calculating whether there are verses before/after the current range.
 */
const calculationTranslation = computed<string>(() => {
  if (props.tids.length === 1) {
    return props.tids[0] ?? props.localTid;
  }
  return props.localTid;
});

/**
 * The new `displayedRange` that we would get if the user clicks the "show more verses before" button.
 */
const displayedRangeExtendedBefore = useComputedResultAsync(() => {
  return props.displayedRange
    .changeTranslation(calculationTranslation.value)
    .addVersesBefore(
      scriptureClient,
      verseIncrement,
      runtimeConfig.public.bible.maxVersesDisplay,
    );
});

/**
 * The new `displayedRange` that we would get if the user clicks the "show more verses after" button.
 */
const displayedRangeExtendedAfter = useComputedResultAsync(() => {
  return props.displayedRange
    .changeTranslation(calculationTranslation.value)
    .addVersesAfter(
      scriptureClient,
      verseIncrement,
      runtimeConfig.public.bible.maxVersesDisplay,
    );
});

/**
 * Whether there are more verses available in the current book before the first displayed verse.
 */
const versesAvailableBefore = computed<boolean>(
  () =>
    !!displayedRangeExtendedBefore.value &&
    props.displayedRange.fromVid !==
      displayedRangeExtendedBefore.value?.fromVid,
);

/**
 * Whether there are more verses available in the current book after the last displayed verse.
 */
const versesAvailableAfter = computed<boolean>(
  () =>
    !!displayedRangeExtendedAfter.value &&
    props.displayedRange.toVid !== displayedRangeExtendedAfter.value?.toVid,
);

/**
 * All items that make up the grid content area.
 */
const verseGridItems = useComputedResultAsync(() =>
  getVerseGridItems(
    scriptureClient,
    props.tids,
    props.displayedRange.fromVid,
    props.displayedRange.toVid,
  ),
);

/**
 * Add some verses after the current text.
 */
function addVersesBelow() {
  if (versesAvailableAfter.value && displayedRangeExtendedAfter.value) {
    emit("update:displayedRange", displayedRangeExtendedAfter.value);
  }
}

/**
 * Add some verses before the current text.
 */
function addVersesAbove() {
  if (versesAvailableBefore.value && displayedRangeExtendedBefore.value) {
    emit("update:displayedRange", displayedRangeExtendedBefore.value);

    // TODO: compensate scroll bar for the content that was added at the top.
    // Like on Whatsapp Web. No idea how they do it yet...
  }
}

const currentColumn = ref(0);
</script>

<style lang="scss">
.fleuron {
  max-height: 40px;

  path {
    fill: rgb(var(--v-theme-primary));
  }
}
</style>
